import { useTranslation } from 'react-i18next';
import { presenceInitials, type RemotePresence } from '@system-atlas/shared';

interface Participant {
  userId: string;
  name: string;
  color: string;
  self?: boolean;
}

/** Dedupliziert Clients nach Benutzer: eine Person mit zwei Tabs = ein Avatar. */
function participants(self: Participant | null, others: RemotePresence[]): Participant[] {
  const map = new Map<string, Participant>();
  if (self) map.set(self.userId, { ...self, self: true });
  for (const o of others) {
    if (!map.has(o.userId)) map.set(o.userId, { userId: o.userId, name: o.name, color: o.color });
  }
  return [...map.values()];
}

/** Praesenzliste (Spec Abschnitt 6): wer ist gerade in der Zeichnung, mit stabiler Farbe.
 *  Phase 4: Klick auf den Avatar einer anderen Person folgt deren Ansicht (Toggle). */
export function PresenceBar({
  self,
  others,
  followingUserId,
  onFollowToggle,
}: {
  self: Participant | null;
  others: RemotePresence[];
  followingUserId?: string | null;
  onFollowToggle?: (userId: string) => void;
}) {
  const { t } = useTranslation();
  const list = participants(self, others);
  if (list.length <= 1) return null; // allein im Dokument: keine Liste noetig
  return (
    <div
      data-testid="presence-bar"
      aria-label={t('editor.presence.title')}
      className="absolute right-3 top-3 z-10 flex items-center -space-x-1.5 rounded-full bg-white/90 px-2 py-1 shadow-sm backdrop-blur dark:bg-slate-800/90"
    >
      {list.map((p) => {
        const followed = followingUserId === p.userId;
        if (p.self || !onFollowToggle) {
          return (
            <span
              key={p.userId}
              title={p.self ? t('editor.presence.you', { name: p.name }) : p.name}
              className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white ring-2 ring-white dark:ring-slate-800"
              style={{ backgroundColor: p.color }}
            >
              {presenceInitials(p.name)}
            </span>
          );
        }
        return (
          <button
            key={p.userId}
            type="button"
            title={p.name}
            aria-label={
              followed
                ? t('editor.presence.stopFollow', { name: p.name })
                : t('editor.presence.follow', { name: p.name })
            }
            aria-pressed={followed}
            onClick={() => onFollowToggle(p.userId)}
            className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white ring-2 focus-visible:outline-none focus-visible:ring-slate-400 ${
              followed ? 'ring-slate-900 dark:ring-white' : 'ring-white dark:ring-slate-800'
            }`}
            style={{ backgroundColor: p.color }}
          >
            {presenceInitials(p.name)}
          </button>
        );
      })}
    </div>
  );
}
